import { JobStatusEnum } from "@/lib/types/job";
import { AgendaJobData } from "../agenda.types";

export const DAY_COL_WIDTH = 112;
export const WEEK_COL_WIDTH = 160;
export const HOUR_COL_WIDTH = 100;
export const HOUR_RANGE_START = 6;
export const HOUR_RANGE_END = 20;

export const colors: Record<number, string> = {
	[JobStatusEnum.REQUESTED]: "bg-gray-400/90 hover:bg-gray-500",
	// [JobStatusEnum.PRE_REGISTERED]: "bg-orange-500/90 hover:bg-orange-600",
	[JobStatusEnum.IN_PROGRESS]: "bg-blue-500/90 hover:bg-blue-600",
	[JobStatusEnum.COMPLETED]: "bg-emerald-500/90 hover:bg-emerald-600",
	[JobStatusEnum.ARCHIVED]: "bg-red-500/90 hover:bg-red-600",
	[JobStatusEnum.BOOKED_SINGLE]: "bg-amber-500/90 hover:bg-amber-600",
	[JobStatusEnum.BOOKED_MONTHLY]: "bg-amber-500/90 hover:bg-amber-600",
	[JobStatusEnum.PAID_SINGLE]: "bg-slate-400/90 hover:bg-slate-500",
	[JobStatusEnum.PAID_MONTHLY]: "bg-slate-400/90 hover:bg-slate-500",
};

export const PENDENT_UNTOUCHED_COLOR = "bg-violet-500/90 hover:bg-violet-600";

// A "Pendent" (IN_PROGRESS) job counts as untouched based on the org's
// untouched-definition config; that boolean is resolved server-side in the RPC.
export const isPendentUntouched = (
	status: number | null,
	isUntouched?: boolean
): boolean => status === JobStatusEnum.IN_PROGRESS && !!isUntouched;

// Single source of truth for a job's bar/dot color: untouched Pendent jobs get
// the violet accent, everything else keeps its status color.
export const getJobColorClass = (
	status: number | null,
	isUntouched?: boolean
): string | undefined =>
	isPendentUntouched(status, isUntouched)
		? PENDENT_UNTOUCHED_COLOR
		: colors[status as number];

export const getAllDaysInRange = (start: Date, end: Date): Date[] => {
	const days: Date[] = [];
	const currentDate = new Date(start);
	while (currentDate <= end) {
		days.push(new Date(currentDate));
		currentDate.setDate(currentDate.getDate() + 1);
	}
	return days;
};

export const formatDate = (date: Date): string => {
	const weekday = date.toLocaleDateString("de-CH", { weekday: "short" });
	const day = date.getDate().toString().padStart(2, "0");
	const month = (date.getMonth() + 1).toString().padStart(2, "0");
	return `${weekday} ${day}.${month}`;
};

export const checkDateInRange = (
	start: Date,
	end: Date,
	checkDate: Date
): number | null => {
	const startDate = new Date(start.toDateString());
	const endDate = new Date(end.toDateString());
	const dateToCheck = new Date(checkDate.toDateString());

	if (dateToCheck.getTime() === startDate.getTime()) {
		return -1;
	} else if (dateToCheck.getTime() === endDate.getTime()) {
		return 1;
	} else if (dateToCheck > startDate && dateToCheck < endDate) {
		return 0;
	} else {
		return null;
	}
};

export interface WeekRange {
	weekNumber: number;
	year: number;
	start: Date;
	end: Date;
}

export const getISOWeekNumber = (date: Date): number => {
	const d = new Date(
		Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
	);
	const dayNum = d.getUTCDay() || 7;
	d.setUTCDate(d.getUTCDate() + 4 - dayNum);
	const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
	return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

const getMonday = (date: Date): Date => {
	const d = new Date(date);
	const day = d.getDay();
	const diff = d.getDate() - day + (day === 0 ? -6 : 1);
	d.setDate(diff);
	d.setHours(0, 0, 0, 0);
	return d;
};

export const getAllWeeksInRange = (start: Date, end: Date): WeekRange[] => {
	const weeks: WeekRange[] = [];
	const current = getMonday(start);

	while (current <= end) {
		const weekStart = new Date(current);
		const weekEnd = new Date(current);
		weekEnd.setDate(weekEnd.getDate() + 6);
		weekEnd.setHours(23, 59, 59, 999);

		weeks.push({
			weekNumber: getISOWeekNumber(weekStart),
			year: weekStart.getFullYear(),
			start: weekStart,
			end: weekEnd,
		});

		current.setDate(current.getDate() + 7);
	}

	return weeks;
};

export const isSameDay = (a: Date, b: Date): boolean =>
	a.getDate() === b.getDate() &&
	a.getMonth() === b.getMonth() &&
	a.getFullYear() === b.getFullYear();

export const isWeekend = (date: Date): boolean => {
	const day = date.getDay();
	return day === 0 || day === 6;
};

export const getAllHoursInRange = (): number[] => {
	const hours: number[] = [];
	for (let h = HOUR_RANGE_START; h <= HOUR_RANGE_END; h++) {
		hours.push(h);
	}
	return hours;
};

export const formatHour = (hour: number): string =>
	`${hour.toString().padStart(2, "0")}:00`;

export const formatJobDateRange = (job: Pick<AgendaJobData, "incoming_order_date" | "outgoing_order_date" | "incoming_order_time" | "outgoing_order_time">): string => {
	if (!job.incoming_order_date || !job.outgoing_order_date) return "";
	const fmtDate = (d: string) => {
		const [y, m, day] = d.split("-");
		return `${day}.${m}.${y}`;
	};
	const start = fmtDate(job.incoming_order_date);
	const end = fmtDate(job.outgoing_order_date);
	const startTime = job.incoming_order_time?.slice(0, 5) || "";
	const endTime = job.outgoing_order_time?.slice(0, 5) || "";
	return `${start}${startTime ? " " + startTime : ""} → ${end}${
		endTime ? " " + endTime : ""
	}`;
};

export const getFontSizeForRowHeight = (rh: number): number => {
	if (rh <= 16) return 9;
	if (rh <= 20) return 10;
	if (rh <= 24) return 11;
	if (rh <= 30) return 12;
	return 13;
};
